import { UploadFile } from 'antd';

import { RepairPictureRawOutputDto } from 'generated/apiSchemas';
import { db, RepairReportPicture } from 'technical/db';
import { filesToPicturesBase64 } from 'technical/picture-upload-input';

export const repairReportPictureSyncService = {
  async download(pictures: RepairPictureRawOutputDto[]): Promise<void> {
    await db.repairReportPictures.bulkPut(
      pictures.map((picture) => ({
        ...picture,
        desynchronizedAt: null,
        deletedAt: null,
      })),
    );
  },

  async createBulk(repairReportId: string, files: UploadFile[]): Promise<void> {
    const existingIds = new Set(
      (await db.repairReportPictures.bulkGet(files.map((file) => file.uid))) // return all object found or undefined
        .filter(Boolean) // remove undefined values from the list
        .map((file) => file!.id), // only keep the id attribute
    );

    const newFiles = files.filter((file) => !existingIds.has(file.uid));
    if (newFiles.length === 0) {
      return;
    }

    const pictures = await filesToPicturesBase64(newFiles);

    await db.repairReportPictures.bulkAdd(
      pictures.map((picture) => ({
        id: picture.id,
        repairReportId,
        data: picture.data,
        desynchronizedAt: new Date().toISOString(),
        deletedAt: null,
      })),
    );
  },

  async getUnsyncedBatch(limit: number): Promise<RepairReportPicture[]> {
    return db.repairReportPictures
      .filter(
        (picture) =>
          picture.desynchronizedAt !== null && picture.deletedAt === null,
      )
      .limit(limit)
      .toArray();
  },

  async markAsSynchronized(ids: string[]): Promise<void> {
    await db.repairReportPictures.bulkUpdate(
      ids.map((id) => {
        return {
          key: id,
          changes: {
            desynchronizedAt: null,
          },
        };
      }),
    );
  },

  async deleteByRepair(repairReportId: string): Promise<void> {
    await db.repairReportPictures
      .where('repairReportId')
      .equals(repairReportId)
      .delete();
  },

  async markForDeletion(id: string): Promise<void> {
    const picture = await db.repairReportPictures.get(id);
    if (!picture) {
      return;
    }

    if (picture.desynchronizedAt === null) {
      await db.repairReportPictures.update(id, {
        deletedAt: new Date().toISOString(),
        desynchronizedAt: new Date().toISOString(),
      });
    } else {
      await db.repairReportPictures.delete(id);
    }
  },

  async getUnsyncedDeletedBatch(limit: number): Promise<RepairReportPicture[]> {
    return db.repairReportPictures
      .filter(
        (picture) =>
          picture.desynchronizedAt !== null && picture.deletedAt !== null,
      )
      .limit(limit)
      .toArray();
  },

  async deleteLocalBatch(ids: string[]): Promise<void> {
    await db.repairReportPictures.bulkDelete(ids);
  },

  async reset(): Promise<void> {
    await db.repairReportPictures.clear();
  },
};
